import { useAppSelector } from '@/store/hooks';
import { useMemo } from 'react';
import { useMainTheme } from '@fuse/core/FuseSettings/hooks/fuseThemeHooks';
import { feedbackThemeUrl } from '@/app/feedback/feedbackTheme';
import i18n from '@i18n';
import useUser from '@auth/useUser';
import { useI18n } from '@i18n/I18nProvider';
import FuseUtils from '@fuse/utils';
import FuseNavigationHelper from '@fuse/utils/FuseNavigationHelper';
import { FuseNavItemType } from '@fuse/core/FuseNavigation/types/FuseNavItemType';
import { selectNavigationAll } from '../store/navigationSlice';
import { useGetUserPermissionsQuery } from '@/app/(control-panel)/admin/settings/SettingsApi';

function useNavigation() {
  const mode = useMainTheme().palette.mode;
  const { data: user } = useUser();
  const userRole = user?.role;
  const { languageId } = useI18n();

  const navigationData = useAppSelector(selectNavigationAll);
  const { data: permissions } = useGetUserPermissionsQuery();
  const canUseAgents = permissions?.tenantFeatures?.agentsEnabled === true &&
    (permissions?.user?.isSuperAdmin === true || permissions?.subcategoryPermissions?.hasAgentsCategory === true);

  const navigation = useMemo(() => {
    const _navigation = FuseNavigationHelper.unflattenNavigation(navigationData);

    function setAdditionalData(data: FuseNavItemType[]): FuseNavItemType[] {
      // Apply at render time too, so initial navigation and archive-save refreshes
      // cannot expose Agents before tenant permissions have loaded.
      return data?.filter((item) => item.id !== 'director-agents-component' || canUseAgents).map((item) => ({
        hasPermission: Boolean(FuseUtils.hasPermission(item?.auth, userRole)),
        ...item,
        ...(item.id === 'admin.feedback' && item.url ? { url: feedbackThemeUrl(item.url, mode) } : {}),
        ...(item?.translate && item?.title ? { title: i18n.t(`navigation:${item?.translate}`) } : {}),
        ...(item?.children ? { children: setAdditionalData(item?.children) } : {})
      }));
    }

    const translatedValues = setAdditionalData(_navigation);

    return translatedValues;
  }, [navigationData, languageId, userRole, canUseAgents, mode]);

  const flattenNavigation = useMemo(() => {
    return FuseNavigationHelper.flattenNavigation(navigation);
  }, [navigation]);

  return { navigation, flattenNavigation };
}

export default useNavigation;
